import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Loader2, X, Upload, Save, ChevronUp, ChevronDown, Image as ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const ProductsAdminPage = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => { loadProducts(); }, []);
  const loadProducts = async () => {
    try { const { data } = await axios.get(`${API}/api/admin/products`, { headers: getHeaders() }); setProducts(data); }
    catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}/api/admin/products/${id}`, { headers: getHeaders() });
    setProducts(p => p.filter(x => x.id !== id));
    setDeleteConfirm(null);
  };

  const toggleStatus = async (p) => {
    const s = p.status === 'active' ? 'hidden' : 'active';
    await axios.put(`${API}/api/admin/products/${p.id}`, { status: s }, { headers: getHeaders() });
    setProducts(prev => prev.map(x => x.id === p.id ? { ...x, status: s } : x));
  };

  const handleReorder = async (p, dir) => {
    const sorted = [...products].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex(x => x.id === p.id);
    const swapIdx = dir === 'up' ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= sorted.length) return;
    const o = sorted[swapIdx];
    await Promise.all([
      axios.put(`${API}/api/admin/products/${p.id}`, { order: o.order }, { headers: getHeaders() }),
      axios.put(`${API}/api/admin/products/${o.id}`, { order: p.order }, { headers: getHeaders() }),
    ]);
    loadProducts();
  };

  const handleSaved = () => { setShowForm(false); setEditingProduct(null); loadProducts(); };
  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;

  return (
    <div data-testid="products-admin-page">
      <div className="flex items-center justify-between mb-8">
        <div><h1 className="text-2xl font-bold text-slate-800">Products</h1><p className="text-sm text-slate-400 mt-1">Manage products (AR/EN)</p></div>
        <button data-testid="add-product-btn" onClick={() => { setEditingProduct(null); setShowForm(true); }}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-medium text-sm px-5 py-2.5 rounded-xl transition-colors">
          <Plus className="w-4 h-4" /> Add Product
        </button>
      </div>

      {products.length === 0 ? (
        <div className="text-center py-20 bg-white rounded-2xl border border-slate-100">
          <ImageIcon className="w-12 h-12 text-slate-200 mx-auto mb-4" />
          <p className="text-slate-400 text-sm">No products yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {[...products].sort((a, b) => a.order - b.order).map(p => (
            <div key={p.id} data-testid={`product-row-${p.id}`} className="flex items-center gap-4 p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
              <div className="flex flex-col gap-0.5">
                <button onClick={() => handleReorder(p, 'up')} className="p-0.5 text-slate-300 hover:text-slate-600"><ChevronUp className="w-4 h-4" /></button>
                <button onClick={() => handleReorder(p, 'down')} className="p-0.5 text-slate-300 hover:text-slate-600"><ChevronDown className="w-4 h-4" /></button>
              </div>
              <div className="w-16 h-12 rounded-lg bg-slate-50 overflow-hidden flex-shrink-0">
                {p.image ? <img src={p.image.startsWith('http') ? p.image : `${API}/api/files/${p.image}`} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><ImageIcon className="w-5 h-5 text-slate-200" /></div>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-700 truncate">{p.name_ar || 'Untitled'}</p>
                <p className="text-xs text-slate-400 truncate">{p.name_en || ''}</p>
              </div>
              <span className="text-xs text-slate-400 bg-slate-50 px-2 py-1 rounded">{p.cta_type}</span>
              <button onClick={() => toggleStatus(p)} className={`px-3 py-1 rounded-full text-xs font-medium ${p.status === 'active' ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
                {p.status === 'active' ? 'Active' : 'Hidden'}
              </button>
              <div className="flex gap-1">
                <button onClick={() => { setEditingProduct(p); setShowForm(true); }} className="p-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg"><Pencil className="w-4 h-4" /></button>
                <button onClick={() => setDeleteConfirm(p.id)} className="p-2 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full mx-4 shadow-2xl">
            <h3 className="text-lg font-bold mb-2">Delete Product?</h3>
            <p className="text-sm text-slate-500 mb-6">This cannot be undone.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirm(null)} className="flex-1 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-200">Cancel</button>
              <button data-testid="confirm-delete-btn" onClick={() => handleDelete(deleteConfirm)} className="flex-1 py-2.5 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600">Delete</button>
            </div>
          </div>
        </div>
      )}

      {showForm && <ProductForm product={editingProduct} onClose={() => { setShowForm(false); setEditingProduct(null); }} onSaved={handleSaved} nextOrder={products.length} />}
    </div>
  );
};

const ProductForm = ({ product, onClose, onSaved, nextOrder }) => {
  const [tab, setTab] = useState('ar');
  const [form, setForm] = useState({
    name_ar: product?.name_ar || '', name_en: product?.name_en || '', slug: product?.slug || '',
    short_desc_ar: product?.short_desc_ar || '', short_desc_en: product?.short_desc_en || '',
    detailed_desc_ar: product?.detailed_desc_ar || '', detailed_desc_en: product?.detailed_desc_en || '',
    features_ar: product?.features_ar?.length ? product.features_ar : [''], features_en: product?.features_en?.length ? product.features_en : [''],
    image: product?.image || '', gallery: product?.gallery || [],
    cta_type: product?.cta_type || 'whatsapp', cta_text_ar: product?.cta_text_ar || 'اطلب عرض سعر', cta_text_en: product?.cta_text_en || 'Request a Quote',
    cta_link: product?.cta_link || '', order: product?.order ?? nextOrder, status: product?.status || 'active',
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const galleryRef = useRef(null);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/admin/products/upload`, fd, { headers: getHeaders() });
      setForm(p => ({ ...p, image: data.path }));
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Upload failed';
      alert(`Upload failed: ${msg}`);
    }
    setUploading(false);
  };

  const handleGalleryUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const { data } = await axios.post(`${API}/api/admin/products/upload`, fd, { headers: getHeaders() });
      setForm(p => ({ ...p, gallery: [...p.gallery, data.path] }));
    } catch { alert('Upload failed'); }
  };

  const isAr = tab === 'ar';
  const lk = isAr ? 'ar' : 'en';

  const updateFeature = (idx, val) => {
    const key = `features_${lk}`;
    setForm(p => { const f = [...p[key]]; f[idx] = val; return { ...p, [key]: f }; });
  };

  const handleSave = async () => {
    if (!form.name_ar.trim() && !form.name_en.trim()) return;
    setSaving(true);
    const payload = { ...form, features_ar: form.features_ar.filter(f => f.trim()), features_en: form.features_en.filter(f => f.trim()) };
    try {
      if (product) await axios.put(`${API}/api/admin/products/${product.id}`, payload, { headers: getHeaders() });
      else await axios.post(`${API}/api/admin/products`, payload, { headers: getHeaders() });
      onSaved();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/30 backdrop-blur-sm overflow-y-auto py-8">
      <div className="bg-white rounded-2xl max-w-2xl w-full mx-4 shadow-2xl">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-lg font-bold text-slate-800">{product ? 'Edit Product' : 'Add Product'}</h2>
          <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Image */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">Main Image</label>
            <div onClick={() => fileRef.current?.click()} className="border-2 border-dashed border-slate-200 rounded-xl aspect-[16/9] flex items-center justify-center cursor-pointer hover:border-blue-300 overflow-hidden">
              {form.image ? <img src={form.image.startsWith('http') ? form.image : `${API}/api/files/${form.image}`} alt="" className="w-full h-full object-cover" /> :
                <div className="text-center">{uploading ? <Loader2 className="w-8 h-8 animate-spin text-blue-400 mx-auto" /> : <Upload className="w-8 h-8 text-slate-300 mx-auto" />}<p className="text-xs text-slate-400 mt-2">Click to upload</p></div>}
            </div>
            <input ref={fileRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
          </div>

          {/* Language Tabs */}
          <div className="flex gap-2 border-b border-slate-100 pb-1">
            <button onClick={() => setTab('ar')} className={`px-4 py-2 text-sm font-medium rounded-t-lg ${isAr ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500' : 'text-slate-400'}`}>Arabic</button>
            <button onClick={() => setTab('en')} className={`px-4 py-2 text-sm font-medium rounded-t-lg ${!isAr ? 'bg-blue-50 text-blue-600 border-b-2 border-blue-500' : 'text-slate-400'}`}>English</button>
          </div>

          <input data-testid="product-name-input" dir={isAr ? 'rtl' : 'ltr'} value={form[`name_${lk}`]} placeholder={`Product Name (${isAr ? 'Arabic' : 'English'}) *`}
            onChange={e => setForm(p => ({ ...p, [`name_${lk}`]: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-blue-400 outline-none" />
          <textarea dir={isAr ? 'rtl' : 'ltr'} value={form[`short_desc_${lk}`]} placeholder={`Short Description (${isAr ? 'Arabic' : 'English'})`} rows={2}
            onChange={e => setForm(p => ({ ...p, [`short_desc_${lk}`]: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-blue-400 outline-none resize-none" />
          <textarea dir={isAr ? 'rtl' : 'ltr'} value={form[`detailed_desc_${lk}`]} placeholder={`Detailed Description (${isAr ? 'Arabic' : 'English'})`} rows={4}
            onChange={e => setForm(p => ({ ...p, [`detailed_desc_${lk}`]: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-blue-400 outline-none resize-none" />

          {/* Features */}
          <div>
            <label className="text-sm font-medium text-slate-600 mb-2 block">Features ({isAr ? 'Arabic' : 'English'})</label>
            {form[`features_${lk}`].map((f, i) => (
              <div key={i} className="flex gap-2 mb-2">
                <input dir={isAr ? 'rtl' : 'ltr'} value={f} onChange={e => updateFeature(i, e.target.value)} placeholder={`Feature ${i + 1}`}
                  className="flex-1 border border-slate-200 rounded-xl px-4 py-2 text-sm focus:border-blue-400 outline-none" />
                {form[`features_${lk}`].length > 1 && <button onClick={() => setForm(p => ({ ...p, [`features_${lk}`]: p[`features_${lk}`].filter((_, j) => j !== i) }))} className="p-2 text-slate-300 hover:text-red-500"><X className="w-4 h-4" /></button>}
              </div>
            ))}
            {form[`features_${lk}`].length < 10 && <button onClick={() => setForm(p => ({ ...p, [`features_${lk}`]: [...p[`features_${lk}`], ''] }))} className="text-xs text-blue-500 font-medium">+ Add Feature</button>}
          </div>

          {/* CTA Settings */}
          <div className="border-t border-slate-100 pt-4 space-y-4">
            <p className="text-xs text-slate-400 font-medium">CTA & Settings</p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">CTA Type</label>
                <select value={form.cta_type} onChange={e => setForm(p => ({ ...p, cta_type: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-blue-400 outline-none bg-white">
                  <option value="whatsapp">WhatsApp</option>
                  <option value="form">Form</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 mb-1 block">Status</label>
                <select value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value }))} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-blue-400 outline-none bg-white">
                  <option value="active">Active</option>
                  <option value="hidden">Hidden</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <input value={form.cta_text_ar} onChange={e => setForm(p => ({ ...p, cta_text_ar: e.target.value }))} dir="rtl" placeholder="CTA Arabic" className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-blue-400 outline-none" />
              <input value={form.cta_text_en} onChange={e => setForm(p => ({ ...p, cta_text_en: e.target.value }))} placeholder="CTA English" className="border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-blue-400 outline-none" />
            </div>
            <input type="number" value={form.order} onChange={e => setForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} placeholder="Display Order" className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm focus:border-blue-400 outline-none" />
          </div>

          {/* Gallery */}
          <div className="border-t border-slate-100 pt-4">
            <label className="text-sm font-medium text-slate-600 mb-2 block">Gallery (optional)</label>
            <div className="flex flex-wrap gap-3">
              {form.gallery.map((img, i) => (
                <div key={i} className="relative w-24 h-20 rounded-lg overflow-hidden border border-slate-100">
                  <img src={img.startsWith('http') ? img : `${API}/api/files/${img}`} alt="" className="w-full h-full object-cover" />
                  <button onClick={() => setForm(p => ({ ...p, gallery: p.gallery.filter((_, j) => j !== i) }))}
                    className="absolute top-0.5 end-0.5 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">x</button>
                </div>
              ))}
              <button onClick={() => galleryRef.current?.click()} className="w-24 h-20 border-2 border-dashed border-slate-200 rounded-lg flex items-center justify-center text-slate-300 hover:border-blue-300 hover:text-blue-400">
                <Plus className="w-5 h-5" />
              </button>
            </div>
            <input ref={galleryRef} type="file" accept="image/*" onChange={handleGalleryUpload} className="hidden" />
          </div>
        </div>

        <div className="p-6 border-t border-slate-100 flex gap-3 justify-end">
          <button onClick={onClose} className="px-5 py-2.5 bg-slate-100 text-slate-600 rounded-xl text-sm font-medium hover:bg-slate-200">Cancel</button>
          <button data-testid="save-product-btn" onClick={handleSave} disabled={saving || (!form.name_ar.trim() && !form.name_en.trim())}
            className="flex items-center gap-2 px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium disabled:opacity-50">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {product ? 'Update' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductsAdminPage;
